import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ListFooter, byLineNo, lineNoNum } from '../ui';

// Stitching-lines Layer 1 — Line Master + Machine Assignment & Status.
// (1) Lines CRUD, (2) per-machine line + status assignment, (3) tracking view
// (per-line composition + idle pool + down/maintenance bucket).
// Write-gated backend-side; UI hides write controls for non-managing roles.
// 'maintenance' status is a FLAG only — not wired to the Maintenance Module.

const MANAGE_ROLES = ['admin', 'management', 'ppc_manager', 'production_incharge', 'production_supervisor'];
const STATUSES = ['running', 'idle', 'down', 'maintenance'];
const STATUS_STYLE = {
  running:     { bg: '#EAF7EE', c: '#166534', label: 'Running' },
  idle:        { bg: '#F7F6F3', c: '#6a6a6a', label: 'Idle' },
  down:        { bg: '#FCEBEB', c: '#A32D2D', label: 'Down' },
  maintenance: { bg: '#FEF3E2', c: '#B45309', label: 'Maintenance' },
};

const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase' };
const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5' };
const badge = (s) => { const t = STATUS_STYLE[s] || STATUS_STYLE.idle; return <span style={{ padding: '2px 9px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: t.bg, color: t.c }}>{t.label}</span>; };

export default function LinesMachines() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);

  const [sub, setSub] = useState('tracking');   // tracking | lines | assign
  const [search, setSearch] = useState('');     // Assignment machine search (client-side)
  const [lines, setLines] = useState([]);
  const [machines, setMachines] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [lineModal, setLineModal] = useState(null);   // form obj (has .id when editing)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    api.get('/ppc/lines-all').then(r => setLines(r.data.data || [])).catch(() => setLines([]));
    api.get('/ppc/machines').then(r => setMachines(r.data.data || [])).catch(() => setMachines([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/users').then(r => setSupervisors((r.data.data || r.data || []).filter(Boolean))).catch(() => {}); }, []);

  const activeLines = lines.filter(l => l.is_active).sort(byLineNo);
  const sortedLines = lines.slice().sort(byLineNo);
  const nextLineNo = () => { const max = lines.reduce((mx, l) => { const n = lineNoNum(l); return n < Infinity && n > mx ? n : mx; }, 0); return `Line ${max + 1}`; };

  const saveLine = async () => {
    setErr(''); setSaving(true);
    try {
      const body = { line_no: lineModal.line_no, operator_capacity: lineModal.operator_capacity, supervisor_id: lineModal.supervisor_id || null, notes: lineModal.notes || null };
      if (lineModal.id) await api.patch('/ppc/lines/' + lineModal.id, body);
      else await api.post('/ppc/lines', body);
      setLineModal(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };
  const toggleLine = async (l) => {
    if (!window.confirm(`${l.is_active ? 'Deactivate' : 'Reactivate'} line "${l.line_no}"?`)) return;
    try { await api.patch('/ppc/lines/' + l.id, { is_active: !l.is_active }); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const setMachine = async (m, patch) => {
    try { await api.patch('/ppc/machines/' + m.id + '/assignment', patch); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const unassigned = machines.filter(m => !m.line_id);
  const byLine = (lineId) => machines.filter(m => m.line_id === lineId);
  const downOrMaint = machines.filter(m => m.status === 'down' || m.status === 'maintenance');
  // Floor-facing identifier: the MANUAL number is primary — handwritten → barcoded →
  // asset_tag (fallback so nothing's blank). mType = the machine kind, shown secondary.
  const mLabel = (m) => m.handwritten_mc_no || m.barcoded_mc_no || m.asset_tag;
  const mType = (m) => m.machine_name || m.machine_code || '';
  // Assignment search — match on machine number (handwritten/barcoded/asset_tag) OR
  // type/name/code. case-insensitive, partial.
  const q = search.trim().toLowerCase();
  const matchMachine = (m) => !q || [m.handwritten_mc_no, m.barcoded_mc_no, m.asset_tag, m.machine_name, m.machine_code]
    .some(v => String(v || '').toLowerCase().includes(q));
  const filteredMachines = machines.filter(matchMachine);

  const subBtn = (k, lbl) => (
    <button onClick={() => setSub(k)} style={{ padding: '8px 16px', border: 'none', background: sub === k ? '#3B3A8F' : '#f0f2f8', color: sub === k ? '#fff' : '#555', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600, marginRight: 8 }}>{lbl}</button>
  );

  return (
    <div>
      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center' }}>
        {subBtn('tracking', '📍 Tracking')}{subBtn('lines', '🏭 Lines')}{subBtn('assign', '🔧 Assignment')}
        {sub === 'assign' && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search machine no. or type…"
              style={{ width: 240, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            {search && <button onClick={() => setSearch('')} style={{ padding: '6px 10px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Clear</button>}
          </div>
        )}
      </div>

      {/* ── TRACKING: per-line composition + idle pool + down/maintenance ── */}
      {sub === 'tracking' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {activeLines.map(l => {
              const ms = byLine(l.id);
              return (
                <div key={l.id} style={{ border: '1px solid #E8E7E7', borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, color: '#2D2C72' }}>🏭 {l.line_no}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>{ms.length} machines</div>
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
                    {Number(l.running_machines) > 0 && <span style={{ marginRight: 8 }}>🟢 {l.running_machines} running</span>}
                    {Number(l.idle_machines) > 0 && <span style={{ marginRight: 8 }}>⚪ {l.idle_machines} idle</span>}
                    {Number(l.down_machines) > 0 && <span style={{ marginRight: 8, color: '#A32D2D' }}>🔴 {l.down_machines} down</span>}
                    {Number(l.maintenance_machines) > 0 && <span style={{ color: '#B45309' }}>🔧 {l.maintenance_machines} maint</span>}
                  </div>
                  {ms.length === 0 ? <div style={{ fontSize: 12, color: '#bbb' }}>No machines assigned</div>
                    : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {ms.map(m => <span key={m.id} title={`${mLabel(m)} · ${mType(m) || '—'} · ${STATUS_STYLE[m.status]?.label}`} style={{ fontSize: 10, fontFamily: 'monospace', padding: '2px 6px', borderRadius: 4, background: STATUS_STYLE[m.status]?.bg, color: STATUS_STYLE[m.status]?.c, fontWeight: 700 }}>{mLabel(m)}</span>)}
                      </div>}
                </div>
              );
            })}
          </div>

          {/* Idle / unassigned pool */}
          <div style={{ marginTop: 18, border: '1px dashed #ccc', borderRadius: 10, padding: 14, background: '#FBFBFB' }}>
            <div style={{ fontWeight: 700, color: '#6a6a6a', marginBottom: 8 }}>⚪ Unassigned / Idle pool — {unassigned.length} machines</div>
            {unassigned.length === 0 ? <div style={{ fontSize: 12, color: '#bbb' }}>All machines assigned.</div>
              : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {unassigned.map(m => <span key={m.id} title={`${mLabel(m)} · ${mType(m) || '—'}`} style={{ fontSize: 10, fontFamily: 'monospace', padding: '2px 6px', borderRadius: 4, background: '#eee', color: '#555' }}>{mLabel(m)}</span>)}
                </div>}
          </div>

          {/* Down / maintenance bucket */}
          <div style={{ marginTop: 14, border: '1px solid #F5C77E', borderRadius: 10, padding: 14, background: '#FFFBF4' }}>
            <div style={{ fontWeight: 700, color: '#B45309', marginBottom: 8 }}>🔧 Down / Maintenance — {downOrMaint.length} machines</div>
            {downOrMaint.length === 0 ? <div style={{ fontSize: 12, color: '#bbb' }}>None.</div>
              : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {downOrMaint.map(m => <span key={m.id} style={{ fontSize: 11, fontFamily: 'monospace', padding: '3px 8px', borderRadius: 4, background: STATUS_STYLE[m.status]?.bg, color: STATUS_STYLE[m.status]?.c, fontWeight: 700 }}>{mLabel(m)}{mType(m) ? ` · ${mType(m)}` : ''} · {m.line_no || 'pool'} · {STATUS_STYLE[m.status]?.label}</span>)}
                </div>}
          </div>
        </div>
      )}

      {/* ── LINES master (CRUD) ── */}
      {sub === 'lines' && (
        <div>
          {canManage && <div style={{ marginBottom: 12, textAlign: 'right' }}>
            <button onClick={() => { setErr(''); setLineModal({ line_no: nextLineNo(), operator_capacity: '', supervisor_id: '', notes: '' }); }} style={{ padding: '7px 16px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>+ New Line</button>
          </div>}
          <div style={{ border: '1px solid #eee', borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['Line', 'Capacity', 'Supervisor', 'Machines', 'Composition', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {lines.length === 0 ? <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 20 }}>No lines yet.</td></tr>
                  : sortedLines.map(l => (
                    <tr key={l.id} style={{ opacity: l.is_active ? 1 : 0.5 }}>
                      <td style={{ ...td, fontWeight: 700, color: '#2D2C72' }}>{l.line_no}</td>
                      <td style={td}>{l.operator_capacity}</td>
                      <td style={td}>{l.supervisor_name || '—'}</td>
                      <td style={td}>{l.assigned_machines}</td>
                      <td style={td}>🟢{l.running_machines} ⚪{l.idle_machines} 🔴{l.down_machines} 🔧{l.maintenance_machines}</td>
                      <td style={td}>{l.is_active ? <span style={{ color: '#166534', fontWeight: 600 }}>Active</span> : <span style={{ color: '#A32D2D' }}>Inactive</span>}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {canManage && <>
                          <button onClick={() => { setErr(''); setLineModal({ id: l.id, line_no: l.line_no, operator_capacity: l.operator_capacity, supervisor_id: l.supervisor_id || '', notes: l.notes || '' }); }} style={{ padding: '3px 9px', background: '#EEEDF8', border: 'none', borderRadius: 4, color: '#3B3A8F', cursor: 'pointer', fontSize: 11, fontWeight: 600, marginRight: 4 }}>Edit</button>
                          <button onClick={() => toggleLine(l)} style={{ padding: '3px 9px', background: l.is_active ? '#FCEBEB' : '#EAF7EE', border: 'none', borderRadius: 4, color: l.is_active ? '#A32D2D' : '#166534', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>{l.is_active ? 'Deactivate' : 'Reactivate'}</button>
                        </>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <ListFooter shown={lines.length} total={lines.length} noun="lines" />
        </div>
      )}

      {/* ── ASSIGNMENT grid ── */}
      {sub === 'assign' && (
        <div>
          {!canManage && <div style={{ background: '#FFF8E6', color: '#9A6B00', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>Read-only — you don't have permission to reassign machines.</div>}
          <div style={{ border: '1px solid #eee', borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['Machine No · Type', 'Code', 'Line', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {filteredMachines.length === 0 ? <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 20 }}>No machines match “{search}”.</td></tr>
                  : filteredMachines.map(m => (
                  <tr key={m.id}>
                    <td style={td}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{mLabel(m)}</span>
                      {mType(m) && <span style={{ color: '#888', fontWeight: 400 }}> · {mType(m)}</span>}
                    </td>
                    <td style={{ ...td, color: '#888' }}>{m.machine_code || '—'}</td>
                    <td style={td}>
                      {canManage
                        ? <select value={m.line_id || ''} onChange={e => setMachine(m, { line_id: e.target.value || null })} style={{ ...inputStyle, width: 150, padding: '4px 8px' }}>
                            <option value="">— idle pool —</option>
                            {activeLines.map(l => <option key={l.id} value={l.id}>{l.line_no}</option>)}
                          </select>
                        : (m.line_no || '— pool —')}
                    </td>
                    <td style={td}>
                      {canManage
                        ? <select value={m.status} onChange={e => setMachine(m, { status: e.target.value })} style={{ ...inputStyle, width: 130, padding: '4px 8px' }}>
                            {STATUSES.map(s => <option key={s} value={s}>{STATUS_STYLE[s].label}</option>)}
                          </select>
                        : badge(m.status)}
                    </td>
                    <td style={td}>{badge(m.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListFooter shown={filteredMachines.length} total={machines.length} noun="machines" />
        </div>
      )}

      {/* ── Line create/edit modal ── */}
      {lineModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 440, maxWidth: '92vw' }}>
            <div style={{ background: '#2D2C72', padding: '14px 22px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>{lineModal.id ? 'Edit Line' : 'New Line'}</h3></div>
            <div style={{ padding: 22 }}>
              {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
              <div style={{ marginBottom: 12 }}><label style={labelStyle}>Line Name / Number *</label><input style={inputStyle} value={lineModal.line_no} onChange={e => setLineModal(m => ({ ...m, line_no: e.target.value }))} placeholder="e.g. Line 1 / Men Lower A" /></div>
              <div style={{ marginBottom: 12 }}><label style={labelStyle}>Operator Capacity</label><input type="number" style={inputStyle} value={lineModal.operator_capacity} onChange={e => setLineModal(m => ({ ...m, operator_capacity: e.target.value }))} /></div>
              <div style={{ marginBottom: 12 }}><label style={labelStyle}>Supervisor</label>
                <select style={inputStyle} value={lineModal.supervisor_id || ''} onChange={e => setLineModal(m => ({ ...m, supervisor_id: e.target.value }))}>
                  <option value="">— none —</option>
                  {supervisors.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select></div>
              <div style={{ marginBottom: 12 }}><label style={labelStyle}>Notes</label><input style={inputStyle} value={lineModal.notes || ''} onChange={e => setLineModal(m => ({ ...m, notes: e.target.value }))} /></div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={() => setLineModal(null)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button disabled={saving} onClick={saveLine} style={{ padding: '8px 20px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{saving ? 'Saving…' : (lineModal.id ? 'Update' : 'Create')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
